import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  required,
  FormDataConsumer,
  SelectInput,
  TextInput,
  ArrayInput,
  RadioButtonGroupInput,
  SimpleFormIterator
} from 'react-admin';
import { Grid, Typography, Button } from '@material-ui/core/';
import { Field, FormSpy } from 'react-final-form';
import { userStatus, userType, userTypeSA, userTypeForIA } from './UserLookups';
import Email from '@material-ui/icons/Email';
import CheckBoxField from '../../core/components/CheckBoxField';
import MailIcon from '@material-ui/icons/Mail';
import DialogContent from '@material-ui/core/DialogContent';
import UserEmailDialog from './UserEmailDialog';
import UserApi from '../../core/apis/UserApi';
import {
  isValidEmailRegex,
  fieldLengthValidator,
  isValidEmailLocalPartAndDomainName,
  isStringContainingLessThanOrGreaterThanOrEqualsSign
} from '../../core/utils/index';
import Confirm from '../../core/components/forked/layout/Confirm';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import TenantConfigurationApi from '../../core/apis/TenantConfigurationApi';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import { useProfile } from '../../core/hooks/UseProfile';

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '0px !important'
  },
  formCheckBox: {
    marginTop: '26px'
  },
  radioGroupRoot: {
    marginTop: '0px'
  },
  emailLinkDiv: {
    display: 'flex',
    alignItems: 'center',
    height: '30px',
    padding: '0 7px',
    borderLeft: '1px solid #E0E0E0'
  },
  welcomeEmailGridStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: '19px'
  },
  welcomeEmailDivStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingBottom: '20px'
  },
  welcomeEmailIcon: {
    color: '#2962FF'
  },
  welcomeEmailIconDisabled: {
    color: '#00000061'
  },
  labelStyle: {
    color: '#2962FF',
    marginLeft: '9px',
    cursor: 'pointer',
    paddingBottom: '2px'
  },
  labelStyleDisabled: {
    color: '#00000061',
    marginLeft: '9px',
    paddingBottom: '2px'
  },
  label: {
    fontSize: '14px',
    transform: 'translate(8px, 5px)',
    marginBottom: '3px'
  },
  gridComponentStyle: {
    paddingLeft: '0px !important',
    paddingTop: '0px !important'
  },
  composeEmailStyle: {
    border: 'solid 1px rgba(0, 0, 0, 0.12)',
    marginTop: '18px'
  },
  addLabel: {
    color: '#2962FF'
  },
  rootFormIterator: {
    marginTop: '8px'
  }
});

const maxSingleAddressLength = 150;

const checkIfEmailIsValidAndExists = async (record, value, profile, props) => {
  if (record && value === record.email) {
    return;
  }

  if (!isValidEmailRegex(value) || !isValidEmailLocalPartAndDomainName(value)) {
    return 'Please enter a valid email';
  }

  let errors = {};
  if (props.active && profile.userType !== 'INVESTOR_ADMIN') {
    let error;
    const emailExists = await UserApi.checkIfEmailExists(
      encodeURIComponent(value)
    );

    if (emailExists) {
      for (var i = 0; i < emailExists.totalCount; i++) {
        if (
          emailExists.pageData[i].email.toUpperCase() === value.toUpperCase()
        ) {
          error = 'Email address already exists';
          break;
        } else {
          error = undefined;
        }
      }
    }

    errors = { ...errors, email: error };
    return error;
  }
  return errors.email;
};

const validateEmail = field => {
  var message;
  if (!isValidEmailRegex(field) === true) {
    message = 'Please enter a valid email address';
  } else if (field && field.length > maxSingleAddressLength) {
    message =
      'Email address should not contain more than ' +
      maxSingleAddressLength +
      ' characters.';
  }
  return message;
};

const UserDetails = ({
  variant,
  creation,
  record,
  handleResend,
  hasComposeEmailPermission,
  hasUserWelcomeEmailPermission,
  userProfile,
  hasEditPermission
}) => {
  const classes = useStyles();
  const { profile } = useProfile();

  const notifyServerError = useNotifyServerError();
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [
    disableComposeEmailAndWelcomeEmail,
    setdisableComposeEmailAndWelcomeEmail
  ] = useState(record.emailOptOut);
  const [openOptOutModal, setOpenOptOutModal] = useState(false);
  const [welcomeEmailValue, setWelcomeEmailValue] = useState('');
  const isMupChild = sessionStorage.getItem('isMupChild') === 'true';
  const checkWelcomeEmailIsOptedOut = (userId, formProps) => {
    if (disableComposeEmailAndWelcomeEmail) {
      setOpenOptOutModal(true);
    } else {
      handleResend(userId, formProps);
    }
  };
  const fetchWelcomeEmailValue = useCallback(() => {
    TenantConfigurationApi.get('Welcome-Email-TemplateId')
      .then(response => setWelcomeEmailValue(response.pageData[0].value))
      .catch(error => notifyServerError(error));
  }, [notifyServerError]);

  useEffect(() => {
    fetchWelcomeEmailValue();
  }, [fetchWelcomeEmailValue]);

  const userTypeChoices = useMemo(() => {
    if (creation) {
      if (userProfile.userType === 'INVESTOR_ADMIN') {
        return userTypeForIA;
      }
      return userProfile.userType === 'SUPER_ADMIN' ? userTypeSA : userType;
    }

    if (userProfile.userType === 'SUPER_ADMIN') {
      if (record.userType === 'REGULAR_CONTACT') {
        return userTypeForIA;
      }
      return userTypeSA;
    }

    if (userProfile.userType === 'FUND_ADMIN') {
      if (record.userType === 'REGULAR_CONTACT') {
        return userTypeForIA;
      }
      return userType;
    }

    if (userProfile.userType === 'INVESTOR_ADMIN') {
      return userTypeForIA;
    }
  }, [userProfile, creation, record]);

  return (
    <FormSpy>
      {formProps => {
        return (
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12} className={classes.headerContainer}>
                <Typography variant="h5">Contact Details</Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="salutationName"
                  label="Salutation"
                  options={{ autoComplete: 'off' }}
                  variant={variant}
                  validate={fieldLengthValidator(100)}
                  id="salutation-name"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="suffix"
                  label="Suffix"
                  options={{ autoComplete: 'off' }}
                  variant={variant}
                  validate={fieldLengthValidator(32)}
                  id="suffix"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={3}
                className={classes.formCheckBox}
              >
                <Field
                  name="receiveDailySummary"
                  component={CheckBoxField}
                  label="Receive Daily Summary"
                  variant={variant}
                  type="checkbox"
                />
              </Grid>
              <Grid
                item
                className={classes.gridComponentStyle}
                xs={12}
                sm={12}
                md={6}
                lg={3}
              >
                {!creation && (
                  <RadioButtonGroupInput
                    source="welcomeMailHasBeenSent"
                    label="Welcome Email Sent"
                    margin="none"
                    classes={{ label: classes.label }}
                    choices={[
                      { id: true, name: 'Yes' },
                      { id: false, name: 'No' }
                    ]}
                    disabled={isMupChild && !hasUserWelcomeEmailPermission}
                    id="welcomeMailHasBeenSent"
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="firstName"
                  validate={[
                    required(),
                    fieldLengthValidator(64),
                    isStringContainingLessThanOrGreaterThanOrEqualsSign
                  ]}
                  label="First Name"
                  options={{ autoComplete: 'off' }}
                  variant={variant}
                  id="first-name"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="title"
                  label="Title"
                  options={{ autoComplete: 'off' }}
                  id="title"
                  variant={variant}
                  validate={[
                    fieldLengthValidator(250),
                    isStringContainingLessThanOrGreaterThanOrEqualsSign
                  ]}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={3}
                className={classes.formCheckBox}
              >
                <Field
                  name="emailOptOut"
                  component={CheckBoxField}
                  label="Email Opt Out"
                  variant={variant}
                  disabled={!creation && !hasEditPermission}
                  onClick={() => {
                    setdisableComposeEmailAndWelcomeEmail(
                      !disableComposeEmailAndWelcomeEmail
                    );
                  }}
                  type="checkbox"
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={3}
                className={classes.welcomeEmailGridStyle}
              >
                {!creation &&
                  hasComposeEmailPermission &&
                  welcomeEmailValue !== '' && (
                    <div
                      id="send-mail"
                      onClick={() =>
                        checkWelcomeEmailIsOptedOut(record.userId, formProps)
                      }
                      className={classes.welcomeEmailDivStyle}
                    >
                      <MailIcon className={classes.welcomeEmailIcon} />
                      <label className={classes.labelStyle}>
                        Send Welcome Email
                      </label>
                    </div>
                  )}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="middleName"
                  label="Middle Name"
                  options={{ autoComplete: 'off' }}
                  variant={variant}
                  id="middle-name"
                  validate={fieldLengthValidator(50)}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="company"
                  label="Company Name"
                  options={{ autoComplete: 'off' }}
                  id="company"
                  variant={variant}
                  validate={fieldLengthValidator(254)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={3}
                className={classes.formCheckBox}
              >
                <Field
                  name="syncToExternalSource"
                  component={CheckBoxField}
                  label="Sync to External Source"
                  variant={variant}
                  type="checkbox"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                {!creation && hasComposeEmailPermission && (
                  <Button
                    variant="outlined"
                    color="primary"
                    id="compose-email"
                    onClick={
                      disableComposeEmailAndWelcomeEmail
                        ? () => setOpenOptOutModal(true)
                        : () => setOpenEmailModal(true)
                    }
                    className={classes.composeEmailStyle}
                    disableFocusRipple
                  >
                    Compose Email
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <TextInput
                  source="lastName"
                  id="last-name"
                  validate={[
                    required(),
                    fieldLengthValidator(64),
                    isStringContainingLessThanOrGreaterThanOrEqualsSign
                  ]}
                  label="Last Name"
                  options={{ autoComplete: 'off' }}
                  variant={variant}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <SelectInput
                  source="userType"
                  validate={[required()]}
                  choices={userTypeChoices}
                  id="user-type"
                  label="User Type"
                  variant={variant}
                  disabled={
                    !(record.userType === 'REGULAR_CONTACT' || creation)
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={3}
                className={classes.formCheckBox}
              >
                <Field
                  name="verifiedByUser"
                  component={CheckBoxField}
                  label="Profile Updated in Portal"
                  variant={variant}
                  type="checkbox"
                  disabled={isMupChild}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3} />
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <FormDataConsumer>
                  {({ formData, ...rest }) => (
                    <TextInput
                      source="email"
                      validate={[
                        required(),
                        fieldLengthValidator(150),
                        (value, _values, props) =>
                          checkIfEmailIsValidAndExists(
                            record,
                            value,
                            profile,
                            props
                          )
                      ]}
                      options={{ autoComplete: 'off' }}
                      variant={variant}
                      InputProps={{
                        endAdornment: (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.emailLinkDiv}
                            href={`mailto:${(formData && formData.email) ||
                              ''}`}
                          >
                            <Email
                              id="email icon"
                              color="action"
                              className={classes.emailIcon}
                            />
                          </a>
                        )
                      }}
                      {...rest}
                    />
                  )}
                </FormDataConsumer>
              </Grid>
              <Grid item sm={12} md={6} lg={3}>
                {!creation && (
                  <SelectInput
                    source="status"
                    choices={userStatus}
                    id="status"
                    validate={[required()]}
                    variant={variant}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3} />
              <Grid item xs={12} sm={12} md={6} lg={3} />
              <Grid item xs={12} sm={12} md={6} lg={3}>
                <ArrayInput
                  label="CC Email(s)"
                  source="ccEmailAddresses"
                  variant={variant}
                  className={classes.rootFormIterator}
                >
                  <SimpleFormIterator
                    // getItemLabel={index => `CC email ${index + 1}`}
                    disableReordering={true}
                    TransitionProps={{ enter: false, exit: false }}
                    addButton={
                      <Button classes={{ root: classes.addLabel }}>
                        <AddIcon />
                        Add CC Email
                      </Button>
                    }
                    removeButton={
                      <Button>
                        <CloseIcon />
                      </Button>
                    }
                  >
                    <TextInput
                      label=""
                      className={classes.ccInput}
                      validate={[required(), validateEmail]}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={3}>
                {!creation && (
                  <TextInput
                    source="userId"
                    label="User ID"
                    id="user-id"
                    validate={[required()]}
                    disabled={true}
                    variant={variant}
                  />
                )}
              </Grid>
            </Grid>
            <DialogContent className={classes.dialogContent}>
              <UserEmailDialog
                open={openEmailModal}
                handleClose={() => setOpenEmailModal(false)}
                cc={record.ccEmailAddresses}
                userProfile={userProfile}
                record={record}
              />
            </DialogContent>
            <Confirm
              onClose={() => setOpenOptOutModal(false)}
              showCancel={false}
              title={'Warning'}
              isOpen={openOptOutModal}
              content={'This user has opted out from receiving emails.'}
              confirm={'OK'}
              onConfirm={() => setOpenOptOutModal(false)}
              confirmColor={'primary'}
            />
          </div>
        );
      }}
    </FormSpy>
  );
};

export default UserDetails;
